"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Activity,
  MessageSquare,
  Upload,
  UserPlus,
  Edit,
  Check,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { ActivityLog, mockActivityService } from "../../utils/mockData";

interface ActivityFeedProps {
  brandId: string;
  limit?: number;
}

export function ActivityFeed({ brandId, limit = 20 }: ActivityFeedProps) {
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, [brandId]);

  const loadActivities = async () => {
    try {
      setIsLoading(true);
      const result = await mockActivityService.getActivityLogs(brandId, limit);
      if (result.error) {
        console.error('Failed to load activities:', result.error);
      } else {
        setActivities(result.activities);
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'commented':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'uploaded':
        return <Upload className="w-4 h-4 text-green-500" />;
      case 'invited':
        return <UserPlus className="w-4 h-4 text-purple-500" />;
      case 'updated':
        return <Edit className="w-4 h-4 text-orange-500" />;
      case 'completed':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'removed':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActivityColor = (action: string) => {
    switch (action) {
      case 'commented':
        return 'text-blue-600';
      case 'uploaded':
        return 'text-green-600';
      case 'invited':
        return 'text-purple-600';
      case 'updated':
        return 'text-orange-600';
      case 'completed':
        return 'text-green-700';
      case 'removed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const activityDate = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - activityDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return activityDate.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center">
          <Activity className="w-5 h-5 mr-2" />
          Recent Activity
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={loadActivities}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No recent activity</p>
            <p className="text-sm text-gray-400">
              Team activities will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity, index) => (
              <div
                key={activity.id}
                className={`flex items-start space-x-3 pb-4 ${
                  index < activities.length - 1 ? 'border-b' : ''
                }`}
              >
                <div className="flex-shrink-0">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={activity.userAvatar} />
                    <AvatarFallback>
                      {activity.userName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    {getActivityIcon(activity.action)}
                    <p className="text-sm">
                      <span className="font-medium">{activity.userName}</span>
                      <span className={`ml-1 ${getActivityColor(activity.action)}`}>
                        {activity.action}
                      </span>
                    </p>
                    <Badge variant="outline" className="text-xs">
                      {formatTimeAgo(activity.timestamp)}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {activity.details}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}